import GlobalTabs from "../../../components/tabs/GlobalTabs";
import SearchBar from "../../../components/searchBar/SearchBar";
import type { ParentProps } from "solid-js";
import { createSignal, For, Show } from "solid-js";
import { softContains } from "../../../utils/stringUtil";
import { isEmpty, some } from "lodash-es";
import CheckboxFieldListItem from "../../../components/listItem/CheckboxFieldListItem";
import { toField } from "../../../fields/fields";
import FormSettingsService from "../../../services/FormSettingsService";
import SubformService from "../../../services/SubformService";
import FormServicesFactory from "../../../services/FormServicesFactory";
import { Accordion } from "../../../components/accordion/Accordion";
import { useTranslation } from "../../../state/i18n";
import EmptyState from "../../../components/emptyState/EmptyState";

const FieldFilterSelection = () => {
  const { t } = useTranslation();

  const [query, setQuery] = createSignal("");

  return (
    <GlobalTabs
      testId="settings-filtered-fields-tabs"
      initialTab="form"
      items={[
        {
          key: "form",
          label: () => t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_FORM_TAB_TITLE),
          Slot: () => (
            <FieldFilterSelectionTab
              query={query()}
              onQueryChange={setQuery}
              placeholder={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_FORM_SEARCH_PLACEHOLDER)}
            >
              <FieldFilterSelectionTabForm query={query()} />
            </FieldFilterSelectionTab>
          ),
        },
        {
          key: "subforms",
          label: () => t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_SUBFORM_TAB_TITLE),
          Slot: () => (
            <FieldFilterSelectionTab
              query={query()}
              onQueryChange={setQuery}
              placeholder={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_SUBFORM_SEARCH_PLACEHOLDER)}
            >
              <FieldFilterSelectionTabSubform query={query()} />
            </FieldFilterSelectionTab>
          ),
        },
      ]}
    />
  );
};

const FieldFilterSelectionTabForm = (props: { query: string }) => {
  const { fieldService: rootFormFieldService } = FormServicesFactory.forRootForm();
  const { t } = useTranslation();

  const checkboxes = () =>
    rootFormFieldService.getFields().filter((fieldConfig) => {
      const field = toField(fieldConfig.type);
      if (!field.hasData || field.type === "signature") {
        return false;
      }

      return (
        softContains(field.displayLabel(fieldConfig.properties), props.query) || softContains(field.type, props.query)
      );
    });

  return (
    <For
      each={checkboxes()}
      fallback={
        <Show
          when={isEmpty(rootFormFieldService.getFields())}
          fallback={
            <EmptyState
              title={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_FORM_EMPTY_STATE_FILTERED_FIELDS_TITLE)}
              description={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_FORM_EMPTY_STATE_FILTERED_FIELDS_DESCRIPTION)}
            />
          }
        >
          <EmptyState
            title={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_FORM_EMPTY_STATE_NO_FIELDS_TITLE)}
            description={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_FORM_EMPTY_STATE_NO_FIELDS_DESCRIPTION)}
          />
        </Show>
      }
    >
      {(fieldConfig) => {
        const field = toField(fieldConfig.type);

        return (
          <CheckboxFieldListItem
            title={field.displayLabel(fieldConfig.properties)}
            dataName={fieldConfig.properties.data_name as string}
            icon={field.icon}
            category={field.category}
            checked={some(FormSettingsService.getSettings().searchSettings.filteredFields, (fieldId) =>
              fieldId.equals(fieldConfig.uid),
            )}
            setChecked={(checked) => {
              if (checked) {
                FormSettingsService.addToFilteredFields(fieldConfig.uid);
                return;
              }
              FormSettingsService.removeFromFilteredFields(fieldConfig.uid);
            }}
          />
        );
      }}
    </For>
  );
};

const FieldFilterSelectionTabSubform = (props: { query: string }) => {
  const { t } = useTranslation();

  const items = () =>
    SubformService.getSubforms()
      .map((subform) => {
        const filteredFields = FormServicesFactory.forSubform(subform.uid)
          .fieldService.getFields()
          .filter(({ type }) => (toField(type).hasData as boolean) && type !== "signature");

        return {
          subform,
          fields: filteredFields,
        };
      })
      .filter((fieldConfig) => !isEmpty(fieldConfig.fields));

  return (
    <Accordion
      variant="list"
      initialClosed={true}
      filterQuery={props.query}
      items={items().map(({ subform, fields: subformFields }) => {
        const filteredFields = FormSettingsService.getSettings().searchSettings.filteredFields;

        return {
          key: subform.uid.toString(),
          label: subform.meta.name,
          components: subformFields.map((subformFieldConfig) => {
            const field = toField(subformFieldConfig.type);

            return {
              Component: () => (
                <CheckboxFieldListItem
                  title={field.displayLabel(subformFieldConfig.properties)}
                  dataName={subformFieldConfig.properties.data_name as string}
                  icon={field.icon}
                  category={field.category}
                  checked={some(filteredFields, (fieldId) => fieldId.equals(subformFieldConfig.uid))}
                  setChecked={(checked) => {
                    checked
                      ? FormSettingsService.addToFilteredFields(subformFieldConfig.uid)
                      : FormSettingsService.removeFromFilteredFields(subformFieldConfig.uid);
                  }}
                />
              ),
              filterKey: (query) =>
                softContains(field.displayLabel(subformFieldConfig.properties), query) ||
                softContains(subformFieldConfig.type, query),
            };
          }),
        };
      })}
      EmptyState={() => (
        <Show
          when={isEmpty(items())}
          fallback={
            <EmptyState
              title={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_SUBFORM_EMPTY_STATE_FILTERED_FIELDS_TITLE)}
              description={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_SUBFORM_EMPTY_STATE_FILTERED_FIELDS_DESCRIPTION)}
            />
          }
        >
          <EmptyState
            title={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_SUBFORM_EMPTY_STATE_NO_SUBFORMS_TITLE)}
            description={t(($) => $.FORM_SETTINGS_FILTERED_FIELDS_SUBFORM_EMPTY_STATE_NO_SUBFORMS_DESCRIPTION)}
          />
        </Show>
      )}
    />
  );
};

type FieldFilterSelectionTabProps = ParentProps<{
  query: string;
  onQueryChange: (query: string) => void;
  placeholder: string;
}>;

const FieldFilterSelectionTab = (props: FieldFilterSelectionTabProps) => (
  <div>
    <div class="my-2">
      <SearchBar query={props.query} onQueryChange={props.onQueryChange} placeholder={props.placeholder} />
    </div>
    <div class="relative max-h-75 overflow-y-auto">{props.children}</div>
  </div>
);

export default FieldFilterSelection;
